import {
  Autocomplete,
  Box,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useState, type FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useAssets } from "../../assets/hooks/use-assets";
import { LexicalEmailEditor } from "../../lexical-email-moment/email-moment-form";
import { ButtonFormContentLibraryLink } from "../../message/editor/components/button/button-form-content-library-link";
import type { SerialisedEditorDesign } from "../../message/editor/types/serialised-editor-design";
import { useFilterTags } from "../../merge-tag/components/use-filter-tags";
import type { Tags } from "../../merge-tag/types";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { useToTarget } from "../../to/use-to-target";
import { useWhatsAppTemplates } from "../hooks/use-whatsapp-templates";
import type { WhatsAppDestinationType } from "../types";
import { WhatsAppContentImport } from "./whatsapp-content-import";
import { WhatsAppTemplatePreview } from "./whatsapp-template-preview";

export const WhatsAppMomentForm: FC<{
  disabled: boolean;
  showValidationErrors: boolean;
  mergeTags: Tags;
}> = ({ disabled, showValidationErrors, mergeTags }) => {
  const { control, getValues, setValue } = useFormContext<MomentFormMoment>();
  const [editorRevision, setEditorRevision] = useState(0);
  useWatch({
    control,
    name: ["whatsapp_template_id", "whatsapp_moment.destination_type"],
  });
  const templateId = getValues("whatsapp_template_id");
  const { data: templates, loading: templatesLoading } = useWhatsAppTemplates(
    templateId ?? undefined,
  );
  const assetsLoader = useAssets();
  const linkableAssetsLoader = assetsLoader.data
    ? {
        ...assetsLoader,
        data: assetsLoader.data.filter((asset) => Boolean(asset.public_url)),
      }
    : assetsLoader;
  const toTarget = useToTarget();
  const filterTags = useFilterTags(
    mergeTags.filter((tag) => tag.version === 2),
    toTarget,
  );

  const destinationType = getValues("whatsapp_moment.destination_type") ?? "page";
  const selectedTemplate = templates?.find(({ id }) => id === templateId) ?? null;

  const selectDestination = (_: unknown, value: WhatsAppDestinationType): void => {
    setValue("whatsapp_moment.destination_type", value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const importPageContent = (design: SerialisedEditorDesign): void => {
    setValue("design", design, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setEditorRevision((revision) => revision + 1);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Template and Link
      </Typography>
      {templatesLoading ? (
        <LinearProgress />
      ) : (
        <Controller
          name="whatsapp_template_id"
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={templates ?? []}
              value={templates?.find(({ id }) => id === field.value) ?? null}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionDisabled={(option) => Boolean(option.deleted_at)}
              onChange={(_, value) => field.onChange(value?.id ?? null)}
              disabled={disabled}
              noOptionsText="No WhatsApp templates are available for this account"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="WhatsApp template"
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    (selectedTemplate?.deleted_at ? "Selected template is unavailable" : undefined)
                  }
                />
              )}
            />
          )}
        />
      )}

      {selectedTemplate && (
        <Box sx={{ mt: 2 }}>
          <WhatsAppTemplatePreview template={selectedTemplate} disabled={disabled} />
        </Box>
      )}

      <Tabs value={destinationType} onChange={selectDestination} sx={{ mt: 3 }}>
        <Tab value="page" label="Link to a page" />
        <Tab value="content_library" label="Link to file in Content Library" />
        <Tab value="url" label="Link to a URL" />
      </Tabs>

      <Box sx={{ pt: 2 }}>
        {destinationType === "page" && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography>Write a page that your WhatsApp Communication links to.</Typography>
              <WhatsAppContentImport disabled={disabled} onImport={importPageContent} />
            </Stack>
            <LexicalEmailEditor
              key={editorRevision}
              disabled={disabled}
              showValidationErrors={showValidationErrors}
              filterTags={filterTags}
              v1Tags={mergeTags.filter((tag) => tag.version === 1)}
              channel="work_email"
              restrictedEmailFrom={[]}
            />
          </>
        )}
        {destinationType === "content_library" && (
          <Controller
            name="whatsapp_moment.digital_asset_id"
            control={control}
            render={({ field, fieldState }) => (
              <ButtonFormContentLibraryLink
                assetsLoader={linkableAssetsLoader}
                selectedAssetTitle={
                  linkableAssetsLoader.data?.find(({ id }) => id === field.value)?.title ?? null
                }
                onUseAsLink={(_, __, asset) => field.onChange(asset.id)}
                onRemoveLink={() => field.onChange(null)}
                formError={fieldState.error}
                useRawAssetPublicUrl
                disabled={disabled}
                buttonVariant="contained"
              />
            )}
          />
        )}
        {destinationType === "url" && (
          <Controller
            name="whatsapp_moment.url"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                label="Destination URL"
                placeholder="https://example.com"
                fullWidth
                disabled={disabled}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        )}
      </Box>
    </Box>
  );
};
